import React from 'react';
import styled, { useTheme } from 'styled-components';
import PropTypes from 'prop-types';
import useStableId from '../../../hooks/useStableId';

// SVG content rendered directly as a component
const CheckedIcon = ({ color, id }) => {
    const maskId = `checkmark-mask-${id}`;
    return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <mask id={maskId}>
                    <rect width="14" height="14" fill="white"/>
                    <path 
                        fillRule="evenodd" 
                        clipRule="evenodd" 
                        d="M4.70708 6.29931C4.61483 6.2038 4.50449 6.12761 4.38249 6.07521C4.26048 6.0228 4.12926 5.99521 3.99648 5.99406C3.8637 5.9929 3.73202 6.0182 3.60913 6.06849C3.48623 6.11877 3.37458 6.19302 3.28069 6.28691C3.18679 6.38081 3.11254 6.49246 3.06226 6.61535C3.01198 6.73825 2.98668 6.86993 2.98783 7.00271C2.98898 7.13549 3.01657 7.26671 3.06898 7.38871C3.12139 7.51072 3.19757 7.62106 3.29308 7.71331L5.29308 9.71331C5.48061 9.90078 5.73492 10.0061 6.00008 10.0061C6.26525 10.0061 6.51955 9.90078 6.70708 9.71331L10.7071 5.71331C10.8026 5.62106 10.8788 5.51072 10.9312 5.38871C10.9836 5.26671 11.0112 5.13549 11.0123 5.00271C11.0135 4.86993 10.9882 4.73825 10.9379 4.61535C10.8876 4.49246 10.8134 4.38081 10.7195 4.28691C10.6256 4.19302 10.5139 4.11877 10.391 4.06849C10.2681 4.0182 10.1365 3.9929 10.0037 3.99406C9.8709 3.99521 9.73968 4.0228 9.61768 4.07521C9.49567 4.12761 9.38533 4.2038 9.29308 4.29931L6.00008 7.59231L4.70708 6.29931Z" 
                        fill="black"
                    />
                </mask>
            </defs>
            <rect width="14" height="14" rx="2" fill={color} mask={`url(#${maskId})`}/>
        </svg>
    );
};

CheckedIcon.propTypes = {
    color: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired
};

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;
    position: relative;
`;

const Label = styled.label`
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
`;

const VisuallyHiddenInput = styled.input`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
    
    &:focus-visible + div {
        outline: 2px solid ${({ theme }) => theme.components.lyt_checkbox.border.focus || '#0063FF'};
        outline-offset: 2px;
    }
    
    &:disabled + div {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const CustomCheckbox = styled.div`
    width: 14px;
    height: 14px;
    box-sizing: border-box;
    flex-shrink: 0;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    /* Show border when unchecked */
    ${({ checked, theme }) => !checked && `
        border: 2px solid ${theme.components.lyt_checkbox.border.default};
        background-color: ${theme.components.lyt_checkbox.background.default};
    `}
    
    /* Hover state for unchecked */
    input:not(:checked):hover + & {
        border-color: ${({ theme }) => theme.components.lyt_checkbox.border.hover};
        background-color: ${({ theme }) => theme.components.lyt_checkbox.background.hover};
    }
    
    /* Focus visible style */
    input:not(:checked):focus-visible + & {
        border-color: ${({ theme }) => theme.components.lyt_checkbox.border.focus};
        background-color: ${({ theme }) => theme.components.lyt_checkbox.background.focus};
    }
`;

const LabelText = styled.span`
    margin-left: 8px;
    font-size: 14px;
`;

const ErrorMessage = styled.div`
    font-size: 12px;
    margin-top: 4px;
`;

/**
 * Accessible checkbox component that uses LYT design system styling
 */
const LytCheckbox = ({
    id,
    name,
    label,
    checked,
    onChange,
    disabled = false,
    required = false,
    error = null,
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy,
    className,
    testId,
    stopPropagation = false,
}) => {
    // Generate a unique ID if one isn't provided
    const uniqueId = useStableId();
    const checkboxId = id || `checkbox-${uniqueId}`;
    const errorId = error ? `${checkboxId}-error` : undefined;
    
    // Access the theme
    const theme = useTheme();
    
    // Combine aria-describedby with error ID if present
    const describedBy = [ariaDescribedBy, errorId].filter(Boolean).join(' ') || undefined;
    
    // Handle checkbox change with event propagation control
    const handleChange = (e) => {
        if (stopPropagation) {
            e.stopPropagation();
        }
        onChange(e);
    };

    // Handle click on the label (needed for integration with components like dropdown)
    const handleLabelClick = (e) => {
        if (stopPropagation) {
            e.stopPropagation();
        }
    };
    
    return (
        <CheckboxContainer 
            className={className} 
            data-testid={testId} 
            onClick={stopPropagation ? e => e.stopPropagation() : undefined}
        >
            <Label 
                htmlFor={checkboxId}
                onClick={handleLabelClick}
            >
                <VisuallyHiddenInput
                    type="checkbox"
                    id={checkboxId}
                    name={name}
                    checked={checked}
                    onChange={handleChange}
                    disabled={disabled}
                    required={required}
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                    aria-describedby={describedBy}
                    aria-invalid={!!error}
                />
                <CustomCheckbox checked={checked}>
                    {checked && <CheckedIcon color={theme.components.lyt_checkbox.checkmark.default} id={checkboxId} />}
                </CustomCheckbox>
                {label && <LabelText>{label}</LabelText>}
            </Label>
            
            {error && (
                <ErrorMessage id={errorId} role="alert">
                    {error}
                </ErrorMessage>
            )}
        </CheckboxContainer>
    );
};

LytCheckbox.propTypes = {
    /** Unique identifier for the checkbox */
    id: PropTypes.string,
    /** Name attribute for the checkbox */
    name: PropTypes.string,
    /** Label text for the checkbox */
    label: PropTypes.node,
    /** Whether the checkbox is checked */
    checked: PropTypes.bool.isRequired,
    /** Function called when the checkbox state changes */
    onChange: PropTypes.func.isRequired,
    /** Whether the checkbox is disabled */
    disabled: PropTypes.bool,
    /** Whether the checkbox is required */
    required: PropTypes.bool,
    /** Error message to display */
    error: PropTypes.string,
    /** Accessible label (when visual label isn't sufficient) */
    ariaLabel: PropTypes.string,
    /** ID of an element that labels this checkbox */
    ariaLabelledBy: PropTypes.string,
    /** ID of an element that describes this checkbox */
    ariaDescribedBy: PropTypes.string,
    /** Additional class name for styling */
    className: PropTypes.string,
    /** Test ID for testing */
    testId: PropTypes.string,
    /** Whether to stop propagation of click and change events */
    stopPropagation: PropTypes.bool
};

export default LytCheckbox;